/**
 * Hook for managing dashboard layout state and persistence.
 * Supports org-level defaults with user-level overrides.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { useOrg } from '@/hooks/useOrg';
import { DEFAULT_WIDGET_ORDER, DEFAULT_HIDDEN_WIDGETS } from '@/components/dashboard/widgetRegistry';
import { useEffect, useState } from 'react';

export interface DashboardLayout {
  widgetOrder: string[];
  hiddenWidgets: string[];
  useOrgDefaults: boolean;
}

export function useDashboardLayout() {
  const { orgId } = useTenant();
  const { isOrgAdmin } = useOrg();
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState<string | null>(null);

  // Get current user ID
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
    });
  }, []);

  // Fetch org default layout
  const { data: orgLayout, isLoading: orgLoading } = useQuery({
    queryKey: ['dashboard-layout-org', orgId],
    queryFn: async () => {
      if (!orgId) return null;
      const { data, error } = await supabase
        .from('dashboard_layouts_org')
        .select('*')
        .eq('org_id', orgId)
        .eq('name', 'default')
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  // Fetch user layout override
  const { data: userLayout, isLoading: userLoading } = useQuery({
    queryKey: ['dashboard-layout-user', orgId, userId],
    queryFn: async () => {
      if (!orgId || !userId) return null;
      const { data, error } = await supabase
        .from('dashboard_layouts_user')
        .select('*')
        .eq('user_id', userId)
        .eq('org_id', orgId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!orgId && !!userId,
  });

  // Compute effective layout (user override > org default > system default)
  const effectiveLayout: DashboardLayout = {
    widgetOrder:
      userLayout?.use_org_defaults === false && userLayout?.widget_order
        ? userLayout.widget_order
        : orgLayout?.widget_order ?? DEFAULT_WIDGET_ORDER,
    hiddenWidgets:
      userLayout?.use_org_defaults === false && userLayout?.hidden_widgets
        ? userLayout.hidden_widgets
        : orgLayout?.hidden_widgets ?? DEFAULT_HIDDEN_WIDGETS,
    useOrgDefaults: userLayout?.use_org_defaults ?? true,
  };

  // Mutation to save user layout
  const saveUserLayout = useMutation({
    mutationFn: async (layout: Partial<DashboardLayout>) => {
      if (!userId || !orgId) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('dashboard_layouts_user')
        .upsert(
          {
            user_id: userId,
            org_id: orgId,
            widget_order: layout.widgetOrder,
            hidden_widgets: layout.hiddenWidgets,
            use_org_defaults: layout.useOrgDefaults ?? false,
          },
          { onConflict: 'user_id,org_id' }
        );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout-user', orgId, userId] });
    },
  });

  // Mutation to save org layout (admin only)
  const saveOrgLayout = useMutation({
    mutationFn: async (layout: { widgetOrder: string[]; hiddenWidgets: string[] }) => {
      if (!orgId || !isOrgAdmin) throw new Error('Not authorized');

      const { error } = await supabase
        .from('dashboard_layouts_org')
        .upsert(
          {
            org_id: orgId,
            name: 'default',
            widget_order: layout.widgetOrder,
            hidden_widgets: layout.hiddenWidgets,
            created_by: userId,
          },
          { onConflict: 'org_id,name' }
        );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout-org', orgId] });
    },
  });

  // Mutation to reset user layout to org defaults
  const resetToOrgDefaults = useMutation({
    mutationFn: async () => {
      if (!userId || !orgId) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('dashboard_layouts_user')
        .upsert(
          {
            user_id: userId,
            org_id: orgId,
            widget_order: null,
            hidden_widgets: null,
            use_org_defaults: true,
          },
          { onConflict: 'user_id,org_id' }
        );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard-layout-user', orgId, userId] });
    },
  });

  return {
    layout: effectiveLayout,
    isLoading: orgLoading || userLoading,
    isOrgAdmin,
    saveUserLayout,
    saveOrgLayout,
    resetToOrgDefaults,
    hasUserCustomization: userLayout?.use_org_defaults === false,
  };
}
